import React, { useEffect, useState } from "react";
import {
  Modal,
  Button,
  Row,
  Col,
  ListGroup,
  OverlayTrigger,
  Tooltip,
  Form,
  Badge,
} from "react-bootstrap";
import {
  ChevronRight,
  ChevronDoubleRight,
  ChevronLeft,
  ChevronDoubleLeft,
  Save,
  X,
  PersonSlash,
  InfoCircleFill,
} from "react-bootstrap-icons";
import "./AssignPatientsModal.css";
import { replaceCaseManagerAssignedCustomers } from "../services/api";
import CaseManagerAssignmentGuide from "./CaseManagerAssignmentGuide/CaseManagerAssignmentGuide";

const formatPatientName = (customer) => {
  const recordId = customer?.recordId ?? customer?.record_id ?? "N/A";
  const firstName =
    customer?.firstName ??
    customer?.first_name ??
    customer?.patientData?.firstName ??
    "";
  const lastName =
    customer?.lastName ??
    customer?.last_name ??
    customer?.patientData?.lastName ??
    "";
  const fullName = [firstName, lastName].filter(Boolean).join(" ");
  return `${recordId} - ${fullName || "N/A"}`;
};

const getAssignedCaseManagerName = (customer) =>
  customer?.assignedCaseManagerName?.trim?.() || "";

function AssignPatientsModal({
  show,
  onHide,
  user,
  initialUnassigned = [],
  initialAssigned = [],
  initialIsLoading = false,
  initialLoadErrorMessage = '',
}) {
  const [unassigned, setUnassigned] = useState([]);
  const [assigned, setAssigned] = useState([]);
  const [selectedUnassigned, setSelectedUnassigned] = useState([]);
  const [selectedAssigned, setSelectedAssigned] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isLoadingAssignable, setIsLoadingAssignable] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showUserGuide, setShowUserGuide] = useState(false);

  const userFullName = [user?.firstName, user?.lastName]
    .filter(Boolean)
    .join(" ");

  const resetLocalState = () => {
    setUnassigned([]);
    setAssigned([]);
    setSelectedUnassigned([]);
    setSelectedAssigned([]);
    setSearchTerm("");
    setShowCancelConfirm(false);
    setShowSuccess(false);
    setShowError(false);
    setErrorMessage("");
    setSuccessMessage("");
    setIsLoadingAssignable(false);
    setIsSaving(false);
  };

  useEffect(() => {
    if (!show) return;

    setShowCancelConfirm(false);
    setShowSuccess(false);
    setShowError(false);
    setErrorMessage("");
    setSuccessMessage("");

    setSelectedUnassigned([]);
    setSelectedAssigned([]);
    setSearchTerm("");

    setIsLoadingAssignable(initialIsLoading);

    if (initialLoadErrorMessage) {
      setErrorMessage(initialLoadErrorMessage);
      setShowError(true);
      setUnassigned([]);
      setAssigned([]);
      setIsLoadingAssignable(false);
      return;
    }

    setUnassigned(initialUnassigned || []);
    setAssigned(initialAssigned || []);
  }, [
    show,
    initialIsLoading,
    initialLoadErrorMessage,
    initialUnassigned,
    initialAssigned,
    user?.id,
  ]);

  const moveSelected = (
    source,
    setSource,
    dest,
    setDest,
    selected,
    setSelected,
  ) => {
    const selectedIds = new Set(selected.map((item) => item.id));
    const moved = source.filter((item) => selectedIds.has(item.id));
    setSource(source.filter((item) => !selectedIds.has(item.id)));
    setDest([...dest, ...moved]);
    setSelected([]);
  };

  const moveAll = (source, setSource, dest, setDest, setSelected) => {
    setDest([...dest, ...source]);
    setSource([]);
    setSelected([]);
  };

  const toggleSelection = (setList, item) => {
    setList((prev) =>
      prev.some((p) => p.id === item.id)
        ? prev.filter((p) => p.id !== item.id)
        : [...prev, item],
    );
  };

  const query = searchTerm.toLowerCase().trim();
  const filteredUnassigned = unassigned.filter((customer) => {
    if (!query) return true;
    return formatPatientName(customer).toLowerCase().includes(query);
  });

  const getCustomerId = (customer) =>
    customer?.id ?? customer?.customerId ?? customer?.customer_id ?? null;

  const handleSave = () => {
    if (!user?.id) return;
    setIsSaving(true);
    setShowError(false);
    setErrorMessage("");

    const customerIds = Array.from(
      new Set(
        assigned
          .map(getCustomerId)
          .filter((id) => id !== null && id !== undefined),
      ),
    );

    const payload = { customerIds };

    replaceCaseManagerAssignedCustomers(user.id, payload)
      .then((response) => {
        const msg =
          response?.data?.message ||
          "Patients assigned to case manager/CRE successfully.";
        setSuccessMessage(msg);
        setShowSuccess(true);
      })
      .catch((e) => {
        console.error("Failed to assign Device Users:", e);
        const msg =
          e?.response?.data?.message ||
          e?.message ||
          "Failed to assign Device Users.";
        setErrorMessage(msg);
        setShowError(true);
      })
      .finally(() => setIsSaving(false));
  };

  const handleClose = () => {
    resetLocalState();
    onHide?.();
  };

  const renderTooltip = (text) => <Tooltip>{text}</Tooltip>;

  return (
    <>
      <Modal
        show={show && !showUserGuide}
        onHide={() => setShowCancelConfirm(true)}
        backdrop="static"
        centered
        dialogClassName="dual-box-modal assign-patients-modal"
      >
        <Modal.Header
          className="justify-content-between align-items-center"
          style={{ backgroundColor: "#3f82da", color: "white", padding: "8px 12px" }}
        >
          <Modal.Title className="mb-0 d-flex align-items-center gap-2" style={{ fontSize: "1.05rem" }}>
            <span>
              Assign Device Users to{" "}
              <span style={{ textTransform: "capitalize" }}>
                {userFullName || "User"}
              </span>
            </span>
            <InfoCircleFill
              size={16}
              style={{ cursor: "pointer", opacity: 0.95 }}
              onClick={() => setShowUserGuide(true)}
              aria-label="Open Case Manager assignment user guide"
              title="User guide"
            />
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="dual-box-modal-body">
          <Row className="dual-list-row">
            <Col lg={5} className="dual-list-panel">
              <div className="dual-list-header">Device Users for Assignment</div>
              <div className="dual-list-container">
                <ListGroup.Item>
                  <Form.Control
                    type="text"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </ListGroup.Item>
                <div className="scrollable-list">
                  <ListGroup style={{ minHeight: "520px" }}>
                    {isLoadingAssignable ? (
                      <ListGroup.Item className="text-muted text-center">
                        Loading...
                      </ListGroup.Item>
                    ) : filteredUnassigned.length === 0 ? (
                      <ListGroup.Item className="text-muted text-center">
                        <div className="empty-box">
                          <PersonSlash className="mb-2" size={20} />
                          <div>No patients found</div>
                        </div>
                      </ListGroup.Item>
                    ) : (
                      filteredUnassigned.map((patient) => (
                        <ListGroup.Item
                          key={patient.id}
                          active={selectedUnassigned.some(
                            (p) => p.id === patient.id,
                          )}
                          onClick={() =>
                            toggleSelection(setSelectedUnassigned, patient)
                          }
                        >
                          <div className="patient-assignment-item">
                            <span className="patient-assignment-item__name">
                              {formatPatientName(patient)}
                            </span>
                          {getAssignedCaseManagerName(patient) && (
                            <Badge
                              bg="warning"
                              text="dark"
                              className="assigned-case-manager-badge"
                            >
                              {getAssignedCaseManagerName(patient)}
                            </Badge>
                          )}
                          </div>
                        </ListGroup.Item>
                      ))
                    )}
                  </ListGroup>
                </div>
              </div>
            </Col>

            <Col
              lg={2}
              className="dual-list-actions d-flex flex-column justify-content-center align-items-center gap-3"
            >
              <OverlayTrigger
                placement="left"
                overlay={renderTooltip("Assign Selected")}
              >
                <Button
                  variant="outline-primary"
                  className="dual-list-action-btn"
                  disabled={selectedUnassigned.length === 0}
                  onClick={() =>
                    moveSelected(
                      unassigned,
                      setUnassigned,
                      assigned,
                      setAssigned,
                      selectedUnassigned,
                      setSelectedUnassigned,
                    )
                  }
                >
                  <ChevronRight />
                </Button>
              </OverlayTrigger>

              <OverlayTrigger
                placement="left"
                overlay={renderTooltip("Assign All")}
              >
                <Button
                  variant="outline-success"
                  className="dual-list-action-btn"
                  disabled={unassigned.length === 0}
                  onClick={() =>
                    moveAll(
                      unassigned,
                      setUnassigned,
                      assigned,
                      setAssigned,
                      setSelectedUnassigned,
                    )
                  }
                >
                  <ChevronDoubleRight />
                </Button>
              </OverlayTrigger>

              <OverlayTrigger
                placement="left"
                overlay={renderTooltip("Unassign Selected")}
              >
                <Button
                  variant="outline-warning"
                  className="dual-list-action-btn"
                  disabled={selectedAssigned.length === 0}
                  onClick={() =>
                    moveSelected(
                      assigned,
                      setAssigned,
                      unassigned,
                      setUnassigned,
                      selectedAssigned,
                      setSelectedAssigned,
                    )
                  }
                >
                  <ChevronLeft />
                </Button>
              </OverlayTrigger>

              <OverlayTrigger
                placement="left"
                overlay={renderTooltip("Unassign All")}
              >
                <Button
                  variant="outline-danger"
                  className="dual-list-action-btn"
                  disabled={assigned.length === 0}
                  onClick={() =>
                    moveAll(
                      assigned,
                      setAssigned,
                      unassigned,
                      setUnassigned,
                      setSelectedAssigned,
                    )
                  }
                >
                  <ChevronDoubleLeft />
                </Button>
              </OverlayTrigger>
            </Col>

            <Col lg={5} className="dual-list-panel">
              <div className="dual-list-header">Assigned Device Users</div>
              <div className="scrollable-list scrollable-list-assigned">
                <ListGroup style={{ minHeight: "520px" }}>
                  {assigned.length === 0 ? (
                    <ListGroup.Item className="text-muted text-center">
                      <div className="empty-box">
                        <PersonSlash className="mb-2" size={20} />
                        <div>No items to display</div>
                      </div>
                    </ListGroup.Item>
                  ) : (
                    assigned.map((patient) => (
                      <ListGroup.Item
                        key={patient.id}
                        active={selectedAssigned.some(
                          (p) => p.id === patient.id,
                        )}
                        onClick={() =>
                          toggleSelection(setSelectedAssigned, patient)
                        }
                      >
                        {formatPatientName(patient)}
                      </ListGroup.Item>
                    ))
                  )}
                </ListGroup>
              </div>
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer className="justify-content-center">
          <Button
            variant="primary"
            onClick={handleSave}
            className="ms-3"
            disabled={isSaving || isLoadingAssignable}
          >
            <Save className="me-2" />
            Save
          </Button>
          <Button
            variant="secondary"
            onClick={() => setShowCancelConfirm(true)}
          >
            <X className="me-2" />
            Cancel
          </Button>
        </Modal.Footer>

        {(showCancelConfirm || showSuccess) && (
          <div className="dim-parent-backdrop" />
        )}
      </Modal>

      <Modal
        show={showCancelConfirm}
        onHide={() => setShowCancelConfirm(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Are you sure?</Modal.Title>
        </Modal.Header>
        <Modal.Body>You want to cancel the process.</Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowCancelConfirm(false)}
          >
            No
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setShowCancelConfirm(false);
              handleClose();
            }}
          >
            Yes
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showSuccess}
        onHide={() => {
          setShowSuccess(false);
          handleClose();
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className="text-success">Success!</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {successMessage ||
            "Patients assigned to case manager/CRE successfully."}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="success"
            onClick={() => {
              setShowSuccess(false);
              handleClose();
            }}
          >
            OK
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showError}
        onHide={() => {
          setShowError(false);
          handleClose();
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">Error</Modal.Title>
        </Modal.Header>
        <Modal.Body>{errorMessage || "Something went wrong."}</Modal.Body>
        <Modal.Footer>
          <Button
            variant="danger"
            onClick={() => {
              setShowError(false);
              handleClose();
            }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <CaseManagerAssignmentGuide
        show={showUserGuide}
        onHide={() => setShowUserGuide(false)}
      />
    </>
  );
}

export default AssignPatientsModal;
